import { NgClass } from '@angular/common';
import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import {
    Component,
    computed,
    DOCUMENT,
    ElementRef,
    inject,
    input,
    output,
    Renderer2,
    signal,
    viewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { ButtonV3Component } from 'components/atoms/button-v3/button-v3.component';
import type { IButtonStyleProperties } from 'components/atoms/button-v3/entities/types';
import { CallToActionsComponent } from 'components/molecules/call-to-action/call-to-action.component';
import { TrialFormV2Component } from 'components/molecules/trial-form-v2/trial-form-v2.component';
import type { ISecondaryNavigation, TPrimaryNavigationItem } from 'components/organisms/header-v2/entities/types';
import { HeaderV2Service } from 'components/organisms/header-v2/service/header-v2.service';
import { HeaderViewService } from 'components/organisms/header-v2/service/header-view.service';
import { LinkTargetDetectorDirective } from 'src/directives/link-target-detector/link-target-detector.directive';
import { SpeculationRulesDirective } from 'src/directives/speculation-rules/speculation-rules.directive';
import { SafeHtmlPipe } from 'src/pipes/safe-html/safe-html.pipe';
import type { ILocale } from 'src/providers/l10n/types';
import { ButtonV3Service } from 'src/services/button/button-v3.service';
import { ThemeService } from 'src/services/theme/theme.service';
import type { ILocaleWithLink } from 'src/services/url-localise/entities';
import { isValidObject } from 'src/shared/helpers/is-valid-object/is-valid-object';
import type { ICallToAction } from 'src/shared/types/call-to-action-item';
import type { TColorScheme, TFullColorScheme, THeaderLayoutTheme } from 'src/shared/types/color-schemes-and-themes';
import type { ILink } from 'src/shared/types/link';
import { EProductType } from 'src/shared/types/product-type';
import { PRODUCT_TYPE } from 'src/tokens/product-type/product-type';

import { HeaderV2SectionPlaceholderComponent } from './header-v2-section-placeholder.component';
import { FourGroupsSectionComponent } from '../four-groups-section/four-groups-section.component';
import { HeaderV2LogoComponent } from '../header-v2-logo/header-v2-logo.component';
import { LanguageSelectorButtonComponent } from '../language-selector-button/language-selector-button.component';
import { LanguageSelectorSectionComponent } from '../language-selector-section/language-selector-section.component';
import { OneGroupAndPromoSectionComponent } from '../one-group-and-promo-section/one-group-and-promo-section.component';
import { TabsGroupsSectionComponent } from '../tabs-groups-section/tabs-groups-section.component';
import { ThreeGroupsAndPromoSectionComponent } from '../three-groups-and-promo/three-groups-and-promo.component';
import { TwoColumnsAndPromoComponent } from '../two-columns-and-promo/two-columns-and-promo.component';
import { TwoGroupsAndBgGroupSectionComponent } from '../two-groups-and-bg-group/two-groups-and-bg-group.component';
import { TwoGroupsAndPromoSectionComponent } from '../two-groups-and-promo-section/two-groups-and-promo-section.component';
import { TwoGroupsThreeAndOneColumnsSectionComponent } from '../two-groups-three-and-one-columns-section/two-groups-three-and-one-columns-section.component';

@Component({
    selector: 'website-header-v2-desktop-menu',
    templateUrl: './header-v2-desktop-menu.component.html',
    styleUrls: ['./header-v2-desktop-menu.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        '(document:keydown.escape)': 'onKeydownHandler()',
    },
    imports: [
        HeaderV2LogoComponent,
        NgClass,
        FourGroupsSectionComponent,
        OneGroupAndPromoSectionComponent,
        TwoGroupsAndPromoSectionComponent,
        TwoGroupsThreeAndOneColumnsSectionComponent,
        LanguageSelectorSectionComponent,
        ButtonV3Component,
        TrialFormV2Component,
        SafeHtmlPipe,
        HeaderV2SectionPlaceholderComponent,
        CallToActionsComponent,
        TabsGroupsSectionComponent,
        TwoGroupsAndBgGroupSectionComponent,
        TwoColumnsAndPromoComponent,
        ThreeGroupsAndPromoSectionComponent,
        SpeculationRulesDirective,
        LanguageSelectorButtonComponent,
        LinkTargetDetectorDirective,
    ],
})
export class HeaderV2DesktopMenuComponent implements OnInit, OnDestroy, AfterViewInit {
    private readonly document = inject<Document>(DOCUMENT);
    private readonly vcr = inject(ViewContainerRef);
    readonly headerService = inject(HeaderV2Service);
    readonly headerViewService = inject(HeaderViewService);
    private readonly renderer = inject(Renderer2);
    private readonly themeService = inject(ThemeService);
    readonly buttonV3Service = inject(ButtonV3Service);
    private readonly productType = inject(PRODUCT_TYPE);

    private componentEl: HTMLElement | null = null;
    private readonly onDestroy$$ = new Subject();

    private trialFormInput: HTMLInputElement | null = null;
    private trialFormTrigger: HTMLElement | null = null;

    private readonly ESCAPE_KEYCODE = 27;
    private readonly TRIAL_FORM_ACTIVATION_CLASS = 'trial-form-opened';
    private readonly HEADER_POPUP_ACTIVATION_CLASS = 'page--nav-opened';
    private readonly HEADER_OVERLAY = 'header__overlay';
    private readonly HEADER_TOP_BANNER_CLASS = 'layout__top-banner';

    public readonly SUBMENU_TRIGGER_BASE_ID = 'header-desktop-submenu-trigger-';
    public readonly SUBMENU_CONTENT_BASE_ID = 'header-desktop-submenu-content-';
    public readonly TRIAL_FORM_TRIGGER_ID = 'header-desktop-trial-form-trigger';
    public readonly LANGUAGE_SELECTOR_LABEL = 'language-selector';
    public readonly themeColors: Record<string, THeaderLayoutTheme> = {
        light: 'light',
        dark: 'dark',
        white: 'light',
        midnight: 'dark',
    };

    public readonly ctaColor: Record<string, TColorScheme> = {
        light: 'white',
        dark: 'midnight',
    };
    public readonly isKlaxoon = this.productType === EProductType.Klaxoon;
    public openedNavigationItem = signal<string>('');
    public isAnyNavItemOpen = signal(false);
    public readonly bottomCtaColorScheme = signal<TColorScheme>('white');
    public readonly buttonStyleProperties = signal<IButtonStyleProperties | undefined>(undefined);

    public readonly primaryNavigation = input<TPrimaryNavigationItem[]>();
    public readonly secondaryNavigation = input<ISecondaryNavigation>();
    public readonly currentLocale = input<ILocale>();
    public readonly theme = input<THeaderLayoutTheme>();
    public readonly languages = input<ILocale[]>([]);

    private readonly themeFromView = signal<THeaderLayoutTheme | null>(null);
    public readonly activeTheme = computed(() => this.themeFromView() ?? this.theme());

    public readonly separatedLanguages = computed(() =>
        this.headerService.separateLanguages((this.languages() as ILocaleWithLink[]) ?? [], 3)
    );

    public readonly trialButtonClickEvent = output<void>();

    private readonly trialForm = viewChild('trialForm', { read: ElementRef });

    onKeydownHandler() {
        this.closeNavigationMenu();
    }

    public ngOnInit(): void {
        this.headerService.state$
            .pipe(
                takeUntil(this.onDestroy$$),
                map((state) => state.isSticky),
                distinctUntilChanged()
            )
            .subscribe(() => {
                this.document.querySelector<HTMLElement>('.' + this.HEADER_OVERLAY)?.addEventListener('click', () => {
                    this.closeNavigationMenu();
                });
            });

        this.headerViewService.theme$.pipe(takeUntil(this.onDestroy$$)).subscribe((theme) => {
            const resolvedTheme = this.themeColors[theme] || this.themeService.getTheme(theme as TFullColorScheme);
            this.themeFromView.set(resolvedTheme);
            this.bottomCtaColorScheme.set(this.ctaColor[resolvedTheme] || 'white');

            this.buttonStyleProperties.set({
                ...this.buttonV3Service.getStylesAccordingToPriority('primary', this.bottomCtaColorScheme()),
                size: 'small',
            });
        });

        if (this.isKlaxoon) {
            this.headerService.setKlaxoonLocale(this.currentLocale(), this.languages());
        }
    }

    public ngAfterViewInit(): void {
        this.componentEl = this.vcr.element.nativeElement;

        const trialForm = this.trialForm();
        if (trialForm) {
            this.trialFormInput = trialForm.nativeElement.querySelector('input');
            this.trialFormTrigger = this.componentEl.querySelector('#' + this.TRIAL_FORM_TRIGGER_ID);
        }
    }

    public ngOnDestroy(): void {
        const trialForm = this.trialForm();
        if (trialForm) {
            trialForm.nativeElement.removeEventListener('focusout', this.handleFocusOutsideTrialForm);
            trialForm.nativeElement.removeEventListener('keydown', this.handleTrialFormEscKeydown);
        }

        this.onDestroy$$.next(null);
        this.onDestroy$$.complete();
    }

    public onClickSubmenu = (currentItem: string): void =>
        this.openedNavigationItem() === currentItem ? this.closeNavigationMenu() : this.openNavigationMenu(currentItem);

    public handleTrialButtonClick(): void {
        if (this.secondaryNavigation().trialForm.hasGoogleAuth) {
            this.trialButtonClickEvent.emit();
        } else {
            this.activateTrialForm();
        }
    }

    private openNavigationMenu = (currentItem: string) => {
        this.setTopPaddingToOverlay();
        this.openedNavigationItem.set(currentItem);
        this.renderer.addClass(this.document.body, this.HEADER_POPUP_ACTIVATION_CLASS);
        this.isAnyNavItemOpen.set(true);
        this.headerService.setDesktopMenuState(true);
    };

    private closeNavigationMenu = () => {
        this.openedNavigationItem.set('');
        this.renderer.removeClass(this.document.body, this.HEADER_POPUP_ACTIVATION_CLASS);
        this.isAnyNavItemOpen.set(false);
        this.headerService.setDesktopMenuState(false);
    };

    public activateTrialForm(): void {
        this.componentEl.classList.add(this.TRIAL_FORM_ACTIVATION_CLASS);

        this.trialFormInput?.focus();

        const trialForm = this.trialForm();
        trialForm.nativeElement.addEventListener('focusout', this.handleFocusOutsideTrialForm);
        trialForm.nativeElement.addEventListener('keydown', this.handleTrialFormEscKeydown);
    }

    private handleFocusOutsideTrialForm = (event: FocusEvent): void => {
        const trialForm = this.trialForm();
        if (
            event.relatedTarget === null ||
            trialForm.nativeElement.contains(event.relatedTarget as HTMLElement) === false
        ) {
            this.deactivateTrialForm();
        }
    };

    private handleTrialFormEscKeydown = (event: KeyboardEvent): void => {
        if (event.keyCode === this.ESCAPE_KEYCODE) {
            this.deactivateTrialForm();

            if (this.trialFormTrigger.focus) {
                this.trialFormTrigger.focus();
            }
        }
    };

    // function to filter empty bottom cta elements (helps in different locales, unfilled cta)
    public filteredCTAList = (ctaList: ICallToAction[]): ICallToAction[] => {
        if (ctaList?.length) {
            return ctaList.filter((cta) => isValidObject(cta));
        }
        return [];
    };

    /**
     * Check if navigation item has url property (is ILink)
     */
    public hasUrl(navigationItem: TPrimaryNavigationItem): boolean {
        return 'url' in navigationItem;
    }

    /**
     * Get url from navigation item
     */
    public getUrl(navigationItem: TPrimaryNavigationItem): string {
        return (navigationItem as ILink).url;
    }

    /**
     * Check if navigation item has subgroups property (is ILinksSubgroups)
     */
    public hasSubgroups(navigationItem: TPrimaryNavigationItem): boolean {
        return 'subgroups' in navigationItem;
    }

    /**
     * Check if navigation item has links property (is ILinksGroup)
     */
    public hasLinks(navigationItem: TPrimaryNavigationItem): boolean {
        return 'links' in navigationItem;
    }

    private deactivateTrialForm(): void {
        const trialForm = this.trialForm();
        if (trialForm && this.trialFormInput.value === '') {
            trialForm.nativeElement.removeEventListener('focusout', this.handleFocusOutsideTrialForm);
            trialForm.nativeElement.removeEventListener('keydown', this.handleTrialFormEscKeydown);
            this.componentEl.classList.remove(this.TRIAL_FORM_ACTIVATION_CLASS);
        }
    }

    private setTopPaddingToOverlay = () => {
        const top_banner = this.document.getElementById(this.HEADER_TOP_BANNER_CLASS);
        const overlay = this.document.getElementById(this.HEADER_OVERLAY);
        if (top_banner) {
            overlay.style.top = top_banner.offsetHeight + 'px';
        }
    };
}
